import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';

export interface Instrument {
    id: string;
    symbol: string;
    name: string;
    price: number;
    change: number;
    changePercent: number;
}

export interface InstrumentsState {
    instruments: Instrument[];
    isFetched: boolean; // Track if initial fetch has been done
}

const initialState: InstrumentsState = {
    instruments: [],
    isFetched: false,
};

const instrumentsSlice = createSlice({
    name: 'instruments',
    initialState,
    reducers: {
        addInstrument: (state, action: PayloadAction<Instrument>) => {
            // Prevent duplicates by checking if symbol already exists
            const exists = state.instruments.some(inst => inst.symbol === action.payload.symbol);
            if (!exists) {
                state.instruments.push(action.payload);
            }
        },
        addInstruments: (state, action: PayloadAction<Instrument[]>) => {
            // Bulk add with duplicate prevention
            action.payload.forEach(newInst => {
                const exists = state.instruments.some(inst => inst.symbol === newInst.symbol);
                if (!exists) {
                    state.instruments.push(newInst);
                }
            });
            state.isFetched = true;
        },
        removeInstrument: (state, action: PayloadAction<string>) => {
            state.instruments = state.instruments.filter(inst => inst.id !== action.payload);
        },
        updateInstrument: (state, action: PayloadAction<Instrument>) => {
            const index = state.instruments.findIndex(inst => inst.id === action.payload.id);
            if (index !== -1) {
                state.instruments[index] = action.payload;
            }
        },
        updateInstrumentPrice: (state, action: PayloadAction<{ id: string; price: number; change: number; changePercent: number }>) => {
            const instrument = state.instruments.find(inst => inst.id === action.payload.id);
            if (instrument) {
                instrument.price = action.payload.price;
                instrument.change = action.payload.change;
                instrument.changePercent = action.payload.changePercent;
            }
        },
        clearInstruments: (state) => {
            state.instruments = [];
            state.isFetched = false;
        },
    },
});

export const { 
    addInstrument,
    addInstruments,
    removeInstrument, 
    updateInstrument, 
    updateInstrumentPrice,
    clearInstruments 
} = instrumentsSlice.actions;

export const selectInstruments = (state: RootState) => state.instruments.instruments;
export const selectIsInstrumentsFetched = (state: RootState) => state.instruments.isFetched;
export const selectInstrumentById = (state: RootState, id: string) => 
    state.instruments.instruments.find(inst => inst.id === id);

export default instrumentsSlice.reducer;
